import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

// Copyright 1999-2020. Plesk International GmbH. All rights reserved.

/* eslint-disable react/no-deprecated */
import { isRtl } from '../../utils';
import React, { Component, cloneElement, Children, Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import ButtonGroup from '../ButtonGroup';
import Input from '../Input';
import Button from '../Button';
import Badge from '../Badge';
import Menu, { MenuItem, MenuSelectableItem } from '../Menu';
import ResponsiveContext from '../ResponsiveContext';
import DistractionFreeModeContext from '../DistractionFreeModeContext';
import { safeInvoke } from '../utils';

/**
 * `SearchBar` component is used for providing type-as-you-go search.
 * @since 0.0.58
 */
var SearchBar =
/*#__PURE__*/
function (_Component) {
  _inherits(SearchBar, _Component);

  function SearchBar() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, SearchBar);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(SearchBar)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _defineProperty(_assertThisInitialized(_this), "state", {
      focused: false,
      overMenu: false,
      value: _this.props.inputProps.value || '',
      highlightedValue: null
    });

    _defineProperty(_assertThisInitialized(_this), "isFocusScheduled", false);

    _defineProperty(_assertThisInitialized(_this), "keyboardNavigation", function (key) {
      var _this$props = _this.props,
          onItemSelect = _this$props.onItemSelect,
          results = _this$props.results;

      var highlightedValue = _this.getHighlightedValue();

      if (!results || !onItemSelect) {
        return;
      }

      var res = _this.getResults();

      var currentIndex = res.indexOf(highlightedValue);
      var nextIndex;

      if (key === 'ArrowUp') {
        nextIndex = Math.max(currentIndex - 1, 0);
      } else if (key === 'ArrowDown') {
        nextIndex = Math.min(currentIndex + 1, res.length - 1);
      }

      _this.setState({
        highlightedValue: currentIndex === -1 ? null : res[nextIndex]
      });
    });

    _defineProperty(_assertThisInitialized(_this), "handleFocus", function () {
      _this.setState({
        focused: true
      });

      _this.search && _this.search.focus();
    });

    _defineProperty(_assertThisInitialized(_this), "handleBlur", function () {
      _this.setState({
        focused: false
      });

      _this.search && _this.search.blur();
    });

    _defineProperty(_assertThisInitialized(_this), "handleMenuMouseOver", function () {
      _this.setState({
        overMenu: true
      });
    });

    _defineProperty(_assertThisInitialized(_this), "handleMenuMouseOut", function () {
      _this.setState({
        overMenu: false
      });
    });

    _defineProperty(_assertThisInitialized(_this), "handleKeyDown", function (e) {
      var onTyping = _this.props.onTyping;
      var value = e.target.value;

      if (['Enter', 'Escape', 'ArrowUp', 'ArrowDown'].indexOf(e.key) !== -1) {
        _this.checkKeys(e.key);

        return;
      }

      _this.setState({
        value: value
      });

      if (onTyping) {
        onTyping(value, function () {
          return _this.clearInput();
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleSearch", function () {
      var _this$props2 = _this.props,
          onSearch = _this$props2.onSearch,
          onTyping = _this$props2.onTyping;
      var value = _this.state.value;

      if (onSearch) {
        onSearch(value, function () {
          return _this.clearInput();
        });
      } else if (onTyping) {
        onTyping(value, function () {
          return _this.clearInput();
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleReset", function () {
      _this.clearInput();

      _this.handleFocus();
    });

    _defineProperty(_assertThisInitialized(_this), "handleSelect", function (value) {
      return function () {
        var onItemSelect = _this.props.onItemSelect;

        _this.setState({
          highlightedValue: null
        });

        _this.handleBlur();

        if (onItemSelect) {
          onItemSelect(value, function () {
            return _this.clearInput();
          });
        } else {
          _this.clearInput();
        }
      };
    });

    _defineProperty(_assertThisInitialized(_this), "handleExpand", function () {
      var _this$props3 = _this.props,
          onExpand = _this$props3.onExpand,
          distractionFreeMode = _this$props3.distractionFreeMode;
      _this.isFocusScheduled = true;
      safeInvoke(onExpand);

      if (distractionFreeMode) {
        distractionFreeMode.toggle();
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleCollapse", function () {
      var _this$props4 = _this.props,
          onCollapse = _this$props4.onCollapse,
          distractionFreeMode = _this$props4.distractionFreeMode;
      safeInvoke(onCollapse);

      if (distractionFreeMode) {
        distractionFreeMode.toggle();
      }
    });

    return _this;
  }

  _createClass(SearchBar, [{
    key: "componentDidUpdate",
    value: function componentDidUpdate() {
      if (this.isFocusScheduled) {
        this.handleFocus();
        this.isFocusScheduled = false;
      }
    }
  }, {
    key: "isResponsive",
    value: function isResponsive() {
      return this.props.responsive;
    }
  }, {
    key: "getResults",
    value: function getResults() {
      var innerResults = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      var results = this.props.results;

      if (!results) {
        return [];
      }

      if (results.type === Menu || innerResults) {
        var res = [];
        var children = innerResults || results.props.children;

        for (var i = 0; i < children.length; i++) {
          var child = children[i];

          if (!child) {
            continue;
          }

          if (child instanceof Array) {
            res = res.concat(this.getResults(child));
          } else if (child.type === MenuItem || child.type === MenuSelectableItem) {
            res.push(child.key);
          }
        }

        return res;
      }

      return results.map(function (item) {
        return item.value;
      });
    }
  }, {
    key: "getHighlightedValue",
    value: function getHighlightedValue() {
      return this.state.highlightedValue || this.getResults()[0];
    }
  }, {
    key: "searchAction",
    value: function searchAction() {
      var _this$props5 = this.props,
          onItemSelect = _this$props5.onItemSelect,
          results = _this$props5.results;
      var highlightedValue = this.getHighlightedValue();

      if (onItemSelect && results) {
        var res = this.getResults();
        var currentIndex = res.indexOf(highlightedValue);

        if (currentIndex === -1) {
          this.handleSearch();
        } else {
          this.handleSelect(highlightedValue)();
        }
      } else {
        this.handleSearch();
      }
    }
  }, {
    key: "checkKeys",
    value: function checkKeys(key) {
      if (key === 'Enter') {
        this.searchAction();
      } else if (key === 'Escape') {
        if (this.state.value) {
          this.handleReset();
        } else {
          this.handleBlur();
          this.handleMenuMouseOut();
        }
      } else if (key === 'ArrowUp' || key === 'ArrowDown') {
        this.keyboardNavigation(key);
      }
    }
  }, {
    key: "clearInput",
    value: function clearInput() {
      this.setState({
        value: ''
      }, this.handleSearch);
    }
  }, {
    key: "elementsIndexToKeyMapping",
    value: function elementsIndexToKeyMapping(elements) {
      var result = [];

      for (var i = 0; i < elements.length; i++) {
        var element = elements[i];

        if (!element) {
          continue;
        }

        if (element instanceof Array) {
          result = result.concat(this.elementsIndexToKeyMapping(element));
        } else {
          result.push(element.key);
        }
      }

      return result;
    }
  }, {
    key: "renderResults",
    value: function renderResults() {
      var _this2 = this;

      var _this$props6 = this.props,
          baseClassName = _this$props6.baseClassName,
          results = _this$props6.results;
      var _this$state = this.state,
          focused = _this$state.focused,
          overMenu = _this$state.overMenu;
      var highlightedValue = this.getHighlightedValue();

      if (!results || results.length === 0) {
        return null;
      }

      return React.createElement(Menu, {
        className: classNames("".concat(baseClassName, "__results"), _defineProperty({}, "".concat(baseClassName, "__results--hidden"), !focused && !overMenu)),
        onMouseOver: this.handleMenuMouseOver,
        onMouseOut: this.handleMenuMouseOut
      }, results.map(function (item) {
        return React.createElement(MenuItem, {
          key: item.value,
          active: item.value === highlightedValue,
          onClick: _this2.handleSelect(item.value)
        }, item.title);
      }));
    }
  }, {
    key: "renderResultsMenu",
    value: function renderResultsMenu() {
      var _this3 = this;

      var _this$props7 = this.props,
          baseClassName = _this$props7.baseClassName,
          results = _this$props7.results,
          onItemSelect = _this$props7.onItemSelect;
      var _this$state2 = this.state,
          focused = _this$state2.focused,
          overMenu = _this$state2.overMenu;
      var highlightedValue = this.getHighlightedValue();

      if (!results) {
        return null;
      }

      return cloneElement(results, {
        className: classNames("".concat(baseClassName, "__results"), _defineProperty({}, "".concat(baseClassName, "__results--hidden"), !focused && !overMenu), results.className),
        onMouseOver: this.handleMenuMouseOver,
        onMouseOut: this.handleMenuMouseOut,
        onItemClick: function onItemClick(item, index) {
          var indexMap = _this3.elementsIndexToKeyMapping(results.props.children);

          _this3.handleSelect(indexMap[index])();
        },
        children: Children.map(results.props.children, function (child) {
          if (onItemSelect && child && (child.type === MenuItem || child.type === MenuSelectableItem)) {
            return cloneElement(child, {
              active: child.key === highlightedValue
            });
          }

          return child;
        })
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _this4 = this;

      var _this$props8 = this.props,
          baseClassName = _this$props8.baseClassName,
          className = _this$props8.className,
          maxWidth = _this$props8.maxWidth,
          minWidth = _this$props8.minWidth,
          loading = _this$props8.loading,
          responsive = _this$props8.responsive,
          onSearch = _this$props8.onSearch,
          onTyping = _this$props8.onTyping,
          onItemSelect = _this$props8.onItemSelect,
          collapsable = _this$props8.collapsable,
          onCollapse = _this$props8.onCollapse,
          onExpand = _this$props8.onExpand,
          results = _this$props8.results,
          inputProps = _this$props8.inputProps,
          distractionFreeMode = _this$props8.distractionFreeMode,
          props = _objectWithoutProperties(_this$props8, ["baseClassName", "className", "maxWidth", "minWidth", "loading", "responsive", "onSearch", "onTyping", "onItemSelect", "collapsable", "onCollapse", "onExpand", "results", "inputProps", "distractionFreeMode"]);

      var _this$state3 = this.state,
          focused = _this$state3.focused,
          value = _this$state3.value;
      var isCollapsed = this.isResponsive();
      var distractionFreeModeEnabled = Boolean(distractionFreeMode && distractionFreeMode.enabled);
      return React.createElement("div", _extends({
        className: classNames(baseClassName, _defineProperty({}, "".concat(baseClassName, "--focused"), focused), className),
        style: isCollapsed || distractionFreeModeEnabled ? null : {
          maxWidth: maxWidth,
          minWidth: minWidth
        }
      }, props), React.createElement(ButtonGroup, {
        className: "".concat(baseClassName, "__group")
      }, isCollapsed && (collapsable || !distractionFreeModeEnabled) ? React.createElement(Badge, {
        intent: "info",
        hidden: !value
      }, React.createElement(Button, {
        icon: "search",
        onClick: this.handleExpand
      })) : React.createElement(Fragment, null, React.createElement(Input, _extends({
        innerRef: function innerRef(ref) {
          return _this4.search = ref;
        },
        className: "".concat(baseClassName, "__input"),
        size: "fill",
        onChange: this.handleKeyDown,
        onFocus: this.handleFocus,
        onBlur: this.handleBlur,
        onKeyDown: this.handleKeyDown
      }, inputProps, {
        value: value,
        prefix: (collapsable && !isCollapsed || distractionFreeModeEnabled) && React.createElement(Button, {
          icon: {
            name: 'chevron-right',
            flipHorizontal: isRtl()
          },
          ghost: true,
          onClick: this.handleCollapse
        }),
        suffix: React.createElement(Fragment, null, value && React.createElement(Button, {
          icon: "remove",
          onClick: this.handleReset,
          ghost: true
        }), React.createElement(Button, {
          icon: "search",
          onClick: this.handleSearch,
          state: loading ? 'loading' : null,
          ghost: true
        }))
      })))), results && results.type === Menu ? this.renderResultsMenu() : this.renderResults());
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(props, state) {
      var _props$inputProps = props.inputProps;
      _props$inputProps = _props$inputProps === void 0 ? {} : _props$inputProps;
      var inputPropsValue = _props$inputProps.value;
      var _state$prevInputProps = state.prevInputProps;
      _state$prevInputProps = _state$prevInputProps === void 0 ? {} : _state$prevInputProps;
      var prevInputPropsValue = _state$prevInputProps.value;
      var nextState = {};

      if (inputPropsValue !== prevInputPropsValue) {
        nextState.value = inputPropsValue;
      }

      nextState.prevInputProps = props.inputProps;
      return nextState;
    }
  }]);

  return SearchBar;
}(Component);

var propTypes = {
  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  baseClassName: PropTypes.string,

  /**
   * Maximum width of SearchBar
   * @since 0.1.0
   */
  maxWidth: PropTypes.number,

  /**
   * Minimum width of SearchBar
   * @since 0.1.0
   */
  minWidth: PropTypes.number,

  /**
   * Should disable input and show spinner or not.
   * @since 0.1.0
   */
  loading: PropTypes.bool,

  /**
   * Should collapse input or not. Used for control in independence of context.
   * @since 0.1.0
   */
  responsive: PropTypes.bool,

  /**
   * Callback for pressing 'Enter' key or 'Search' button.
   *
   * arguments:
   * * value - search value
   * * clearHandler - function that clears input value
   * @since 0.1.0
   */
  onSearch: PropTypes.func,

  /**
   * Callback for pressing any key in input. Used for a live search.
   *
   * arguments:
   * * value - search value
   * * clearHandler - function that clears input value
   * @since 0.1.0
   */
  onTyping: PropTypes.func,

  /**
   * Callback for selecting menu item with mouse click or key pressing.
   *
   * arguments:
   * * value - selected value
   * * clearHandler - function that clears input value
   * @since 0.1.0
   */
  onItemSelect: PropTypes.func,

  /**
   * Should render toggle button or not.
   * @since 2.2.0
   */
  collapsable: PropTypes.bool,

  /**
   * Callback for pressing toggle button (in normal mode).
   * @since 2.2.0
   */
  onCollapse: PropTypes.func,

  /**
   * Callback for pressing toggle button (in collapsed mode).
   * @since 2.2.0
   */
  onExpand: PropTypes.func,

  /**
   * Results to display.
   * Could be specified by array of objects with "value" and "title" properties or by Menu component.
   * If Menu component is used then key property of MenuItem will be treated as value.
   * @since 0.1.0
   */
  results: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  })), PropTypes.element]),

  /**
   * Props of underlying input element.
   * @since 0.4.1
   */
  inputProps: PropTypes.object
};
var defaultProps = {
  className: undefined,
  baseClassName: "".concat(CLS_PREFIX, "search-bar"),
  maxWidth: 500,
  minWidth: 270,
  loading: false,
  responsive: undefined,
  onSearch: undefined,
  onTyping: undefined,
  onItemSelect: undefined,
  collapsable: false,
  onCollapse: undefined,
  onExpand: undefined,
  results: undefined,
  inputProps: {}
};
SearchBar.propTypes = _objectSpread({}, propTypes, {
  distractionFreeMode: PropTypes.shape({
    enabled: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired
  })
});
SearchBar.defaultProps = {
  distractionFreeMode: undefined
};

var SearchBarWrapper = function SearchBarWrapper(props) {
  var distractionFreeMode = useContext(DistractionFreeModeContext);
  var responsive = useContext(ResponsiveContext);
  responsive = props.responsive === undefined ? responsive || false : Boolean(props.responsive);
  return React.createElement(SearchBar, _extends({}, props, {
    responsive: responsive,
    distractionFreeMode: distractionFreeMode
  }));
};

SearchBarWrapper.displayName = 'SearchBar';
SearchBarWrapper.propTypes = propTypes;
SearchBarWrapper.defaultProps = defaultProps;
export default SearchBarWrapper;